/**
 * React Query hooks for agent identity operations.
 * Provides declarative data fetching with caching and automatic refetching.
 *
 * @module hooks/useAgentIdentities
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { agentIdentityService, ApiException } from '@/services';
import { showErrorToast } from '@/utils/errorToast';
import type {
  AgentIdentityCreateRequest,
  AgentIdentityUpdateRequest,
  AgentIdentityResponse,
} from '@/types';

/**
 * Query key factory for agent identities.
 * Ensures consistent cache key generation.
 */
export const agentIdentityKeys = {
  all: ['agentIdentities'] as const,
  lists: () => [...agentIdentityKeys.all, 'list'] as const,
  list: (page?: number, pageSize?: number) =>
    [...agentIdentityKeys.lists(), { page, pageSize }] as const,
  details: () => [...agentIdentityKeys.all, 'detail'] as const,
  detail: (id: string) => [...agentIdentityKeys.details(), id] as const,
};

/**
 * Hook to fetch agent identities list
 *
 * @param page - Page number (1-based)
 * @param pageSize - Number of items per page
 * @example
 * const { data, isLoading, error } = useAgentIdentities(1, 25);
 */
export function useAgentIdentities(page: number = 1, pageSize: number = 25) {
  return useQuery({
    queryKey: agentIdentityKeys.list(page, pageSize),
    queryFn: () => agentIdentityService.list(page, pageSize),
  });
}

/**
 * Hook to fetch a single agent identity by ID
 *
 * @param id - The agent identity ID
 * @example
 * const { data, isLoading, error } = useAgentIdentity('my-identity-id');
 */
export function useAgentIdentity(id: string) {
  return useQuery({
    queryKey: agentIdentityKeys.detail(id),
    queryFn: () => agentIdentityService.getById(id),
    enabled: !!id,
  });
}

/**
 * Hook to create an agent identity
 *
 * @example
 * const { mutate, isPending } = useCreateAgentIdentity();
 * mutate(request, { onSuccess: () => navigate('/agent-identities') });
 */
export function useCreateAgentIdentity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: AgentIdentityCreateRequest) => agentIdentityService.create(request),
    onSuccess: (data: AgentIdentityResponse) => {
      queryClient.invalidateQueries({ queryKey: agentIdentityKeys.lists() });
      queryClient.setQueryData(agentIdentityKeys.detail(data.id), data);
      toast.success(`Agent identity "${data.displayName}" created successfully`);
    },
    onError: (error: Error) => {
      const message = error instanceof ApiException && error.detail ? error.detail : error.message;
      showErrorToast(`Failed to create agent identity: ${message}`);
    },
  });
}

/**
 * Hook to delete an agent identity
 *
 * @example
 * const { mutate, isPending } = useDeleteAgentIdentity();
 * mutate('my-identity-id');
 */
export function useDeleteAgentIdentity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => agentIdentityService.delete(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: agentIdentityKeys.lists() });
      queryClient.removeQueries({ queryKey: agentIdentityKeys.detail(id) });
      toast.success('Agent identity deleted successfully');
    },
    onError: (error: Error) => {
      const message = error instanceof ApiException && error.detail ? error.detail : error.message;
      showErrorToast(`Failed to delete agent identity: ${message}`, { duration: 6000 });
    },
  });
}

/**
 * Hook to update an agent identity (add/remove agent user)
 *
 * @example
 * const { mutate, isPending } = useUpdateAgentIdentity();
 * mutate({ id: 'my-identity-id', request: { agentUser: { userPrincipalName: 'user@domain.com' } } });
 */
export function useUpdateAgentIdentity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, request }: { id: string; request: AgentIdentityUpdateRequest }) =>
      agentIdentityService.update(id, request),
    onSuccess: (data: AgentIdentityResponse) => {
      queryClient.invalidateQueries({ queryKey: agentIdentityKeys.lists() });
      queryClient.setQueryData(agentIdentityKeys.detail(data.id), data);
      if (data.agentUser) {
        toast.success(`Agent user "${data.agentUser.displayName}" added successfully`);
      } else {
        toast.success('Agent user removed successfully');
      }
    },
    onError: (error: Error) => {
      const isConflict = error instanceof ApiException && error.status === 409;
      if (isConflict) {
        showErrorToast(
          'Agent identity has not propagated yet. Please wait a few seconds and try again.',
          { duration: 8000 }
        );
      } else {
        const message =
          error instanceof ApiException && error.detail ? error.detail : error.message;
        showErrorToast(`Failed to update agent identity: ${message}`);
      }
    },
  });
}
